import { oc } from '@orpc/contract';
import { z } from 'zod';

/**
 * 年末調整 CSV 非同期エクスポート API（oRPC）のローカルミラー契約。
 *
 * BE 契約の正:
 *   dresscode-backend v2/src/hr-force/api/year-end-adjustment-csv/usecase/
 *     year-end-adjustment-csv-export.api-contract.ts
 * この定義は上記 BE 契約と厳密一致させる（`@standandforce/api-contract` に publish
 * されたら本ローカルミラーを廃止する）。
 *
 * exportId を「エクスポートセッション」とし、partition / generate / finalize の 3 ステージを
 * 単一の BatchJob に束ねる（`exportId : jobId = 1:1`）。ステータスは DB に保存せず
 * `batchJob.status` から導出する（People CSV 非同期取り込みと同思想）。
 *   ① enqueue      : 対象確定 + セッション作成 + ジョブ投入（多重実行ガード / 0 件ガードあり）
 *   ② latestByTag  : タグの直近セッション（実行中 or 直近完了）を再アタッチ用に取得
 *   ③ detail       : exportId のセッション詳細（導出ステータス・進捗）をポーリング取得
 *   ④ download     : SUCCEEDED セッションの最終ファイルの署名 URL（30 秒）を取得
 *
 * すべて ApiContext の organizationId にスコープし、他テナントの exportId は返さない。
 */

// ── Enums ──

/**
 * 一覧と共有するフィルター enum。
 *
 * 一覧側の契約は `@standandforce/api-contract` へ移行済みだが、そちらは Zod Schema を
 * 公開していないため、BE 契約（`list-year-end-adjustment-instances-by-tag.api-contract.ts` の
 * `yearEndAdjustmentDocumentItemKeyValues` / `yearEndAdjustmentOverallStatusValues`）と
 * 同順・同値で本ミラーに定義する。BE 側で値を追加したら本定義も更新すること。
 */
export const YearEndAdjustmentDocumentFocusSchema = z.enum(['dependentDeduction', 'dependentDeductionNextYear', 'spouseDeduction', 'insurancePremium', 'housingLoan', 'previousEmployer']);
export type YearEndAdjustmentDocumentFocus = z.infer<typeof YearEndAdjustmentDocumentFocusSchema>;
export const YearEndAdjustmentOverallStatusSchema = z.enum(['completed', 'returned', 'awaitingReview', 'pendingAction']);

/**
 * エクスポートの導出ステータス（DB には保存せず `batchJob.status` から導出）。
 * 非終端: PENDING / RUNNING。終端: SUCCEEDED / FAILED / PARTIALLY_FAILED / CANCELLED。
 */
export const YearEndAdjustmentCsvExportStatusSchema = z.enum(['PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED', 'PARTIALLY_FAILED', 'CANCELLED']);

/** Stage 状態（Prisma `BatchJobStageStatus`）。 */
export const YearEndAdjustmentCsvExportStageStatusSchema = z.enum(['PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED', 'PARTIALLY_FAILED', 'CANCELLED', 'SKIPPED']);

/** CSV の文字エンコーディング。 */
export const YearEndAdjustmentCsvExportEncodingSchema = z.enum(['UTF-8', 'Shift_JIS', 'EUC-JP']);

// ── 共通サブスキーマ ──

/** Stage 概要（進捗表示用）。partition / generate / finalize の 3 ステージ。 */
const YearEndAdjustmentCsvExportStageSchema = z.object({
  order: z.number(),
  handlerKey: z.string(),
  name: z.string(),
  status: YearEndAdjustmentCsvExportStageStatusSchema,
  chunkCount: z.number(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable()
});

/** チャンクの状態別集計（進捗バー用）。 */
const YearEndAdjustmentCsvExportChunkCountsSchema = z.object({
  total: z.number(),
  succeeded: z.number(),
  running: z.number(),
  pending: z.number(),
  failed: z.number()
});

/** 進捗サマリ（Stage 一覧 + チャンク集計）。 */
const YearEndAdjustmentCsvExportProgressSchema = z.object({
  stages: z.array(YearEndAdjustmentCsvExportStageSchema),
  chunkCounts: YearEndAdjustmentCsvExportChunkCountsSchema
});

/**
 * エクスポートセッション詳細（detail / latestByTag で共通）。
 * Optional 項目は null で表現する。
 */
export const YearEndAdjustmentCsvExportSessionSchema = z.object({
  exportId: z.string(),
  tagId: z.string(),
  targetYear: z.number(),
  /** 導出されたセッション状態（`batchJob.status`）。 */
  status: YearEndAdjustmentCsvExportStatusSchema,
  /** Stage1（partition）で確定する対象件数（未確定は null）。 */
  totalRowCount: z.number().nullable(),
  /** ユーザーに提示する CSV ファイル名（例: 年末調整_{タグ名}.csv）。 */
  originalFileName: z.string(),
  encoding: YearEndAdjustmentCsvExportEncodingSchema,
  /** batch_job 由来の失敗理由コード（PII を含まないコード文字列）。成功・進行中は null。 */
  errorCode: z.string().nullable(),
  /** 最終ファイルが生成済みでダウンロード可能か（SUCCEEDED かつ finalFileKey 確定）。 */
  downloadReady: z.boolean(),
  progress: YearEndAdjustmentCsvExportProgressSchema,
  /** ISO 8601 文字列。 */
  createdAt: z.string(),
  updatedAt: z.string(),
  /** ジョブ完了日時（未完了は null）。ISO 8601 文字列。 */
  completedAt: z.string().nullable()
});

// ── ① enqueue ──

/**
 * enqueue の入力（画面フィルターのスナップショット）。
 * status / documentFocus は一覧契約と同じ enum を共有し、対象がズレないようにする。
 * targetYear はタグから BE が解決するため入力に含めない。
 */
const EnqueueYearEndAdjustmentCsvExportInputSchema = z.object({
  tagId: z.string(),
  searchText: z.string().max(500).optional(),
  status: z.array(YearEndAdjustmentOverallStatusSchema).optional(),
  documentFocus: YearEndAdjustmentDocumentFocusSchema.optional(),
  /** 省略時は UTF-8。 */
  encoding: YearEndAdjustmentCsvExportEncodingSchema.optional()
});
const EnqueueYearEndAdjustmentCsvExportOutputSchema = z.object({
  exportId: z.string()
});

// ── ② latestByTag ──

const GetLatestYearEndAdjustmentCsvExportInputSchema = z.object({
  tagId: z.string()
});
const GetLatestYearEndAdjustmentCsvExportOutputSchema = z.object({
  /** タグの直近セッション（実行中 or 直近完了）。存在しなければ null。 */
  session: YearEndAdjustmentCsvExportSessionSchema.nullable()
});

// ── ③ detail ──

const GetYearEndAdjustmentCsvExportDetailInputSchema = z.object({
  exportId: z.string()
});

// ── ④ download ──

const DownloadYearEndAdjustmentCsvExportInputSchema = z.object({
  exportId: z.string()
});
const DownloadYearEndAdjustmentCsvExportOutputSchema = z.object({
  /** S3 署名 URL（有効期限 30 秒）。 */
  url: z.string(),
  /** ダウンロード時のファイル名。 */
  fileName: z.string()
});

// ── Contract ──
//
// NOTE: latestByTag（リテラル `/latest`）は detail（`/{exportId}`）より先に定義する
// （BE 側で Express の登録順先勝ちによるルート衝突回避のため。順序を BE と揃える）。
export const laborCoreYearEndAdjustmentCsvExportContract = oc.router({
  enqueue: oc.route({
    method: 'POST',
    path: '/v2/labor-core/year-end-adjustment/csv-exports'
  }).input(EnqueueYearEndAdjustmentCsvExportInputSchema).output(EnqueueYearEndAdjustmentCsvExportOutputSchema),
  latestByTag: oc.route({
    method: 'GET',
    path: '/v2/labor-core/year-end-adjustment/csv-exports/latest'
  }).input(GetLatestYearEndAdjustmentCsvExportInputSchema).output(GetLatestYearEndAdjustmentCsvExportOutputSchema),
  detail: oc.route({
    method: 'GET',
    path: '/v2/labor-core/year-end-adjustment/csv-exports/{exportId}'
  }).input(GetYearEndAdjustmentCsvExportDetailInputSchema).output(YearEndAdjustmentCsvExportSessionSchema),
  download: oc.route({
    method: 'GET',
    path: '/v2/labor-core/year-end-adjustment/csv-exports/{exportId}/download'
  }).input(DownloadYearEndAdjustmentCsvExportInputSchema).output(DownloadYearEndAdjustmentCsvExportOutputSchema)
});

// ── 型エクスポート ──

export type YearEndAdjustmentCsvExportStatus = z.infer<typeof YearEndAdjustmentCsvExportStatusSchema>;
export type YearEndAdjustmentCsvExportSession = z.infer<typeof YearEndAdjustmentCsvExportSessionSchema>;
export type YearEndAdjustmentCsvExportEncoding = z.infer<typeof YearEndAdjustmentCsvExportEncodingSchema>;
export type EnqueueYearEndAdjustmentCsvExportInput = z.infer<typeof EnqueueYearEndAdjustmentCsvExportInputSchema>;
export type EnqueueYearEndAdjustmentCsvExportOutput = z.infer<typeof EnqueueYearEndAdjustmentCsvExportOutputSchema>;
export type GetLatestYearEndAdjustmentCsvExportOutput = z.infer<typeof GetLatestYearEndAdjustmentCsvExportOutputSchema>;
export type DownloadYearEndAdjustmentCsvExportOutput = z.infer<typeof DownloadYearEndAdjustmentCsvExportOutputSchema>;